// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - API slice for Session Reference Content
//           including Videos, Articles, Blogs and other resources


import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'


import {
    apiSlice,
} from '@/app/api/apiSlice'



/*
|--------------------------------------------------------------------------
| 01 - Entity Adapter
|--------------------------------------------------------------------------
*/

const sessionReferenceContentAdapter =
    createEntityAdapter({})


const initialState =
    sessionReferenceContentAdapter
        .getInitialState()



/*
|--------------------------------------------------------------------------
| 02 - API Slice
|--------------------------------------------------------------------------
*/

export const sessionReferenceContentApiSlice =
    apiSlice.injectEndpoints({

        endpoints:
            builder => ({


                /*
                |--------------------------------------------------------------------------
                | 01 - Get Session Reference Content
                |--------------------------------------------------------------------------
                */

                getSessionReferenceContent:
                    builder.query({

                        query: ({
                            sessionId = '',
                            contentType = '',
                            active = '',
                        } = {}) => ({

                            url:
                                '/sessionReferenceContent/getSessionReferenceContent',

                            params: {

                                ...(sessionId
                                    ? {
                                        sessionId
                                    }
                                    : {}),


                                ...(contentType
                                    ? {
                                        contentType
                                    }
                                    : {}),


                                ...(active !== ''
                                    ? {
                                        active
                                    }
                                    : {}),

                            },


                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )

                            },

                        }),


                        /*
                        |--------------------------------------------------------------------------
                        | Normalize Backend Array
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => {

                                const content =
                                    Array.isArray(
                                        responseData
                                    )
                                        ?
                                        responseData
                                        :
                                        []


                                const loadedContent =
                                    content.map(
                                        item => ({

                                            ...item,

                                            id:
                                                item._id,

                                        })
                                    )


                                return sessionReferenceContentAdapter
                                    .setAll(
                                        initialState,
                                        loadedContent
                                    )

                            },


                        providesTags: (
                            result
                        ) => {

                            if (
                                result?.ids
                            ) {

                                return [

                                    {
                                        type:
                                            'SessionReferenceContent',

                                        id:
                                            'LIST',
                                    },


                                    ...result.ids.map(
                                        id => ({

                                            type:
                                                'SessionReferenceContent',

                                            id,

                                        })
                                    ),

                                ]

                            }


                            return [

                                {
                                    type:
                                        'SessionReferenceContent',

                                    id:
                                        'LIST',
                                },

                            ]

                        },

                    }),



                /*
                |--------------------------------------------------------------------------
                | 02 - Get Session Reference Content By ID
                |--------------------------------------------------------------------------
                */

                getSessionReferenceContentById:
                    builder.query({

                        query: (id) => ({

                            url:
                                `/sessionReferenceContent/getSessionReferenceContentById/${id}`,

                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )

                            },

                        }),


                        transformResponse:
                            responseData => {

                                if (
                                    !responseData
                                ) {
                                    return null
                                }

                                return {

                                    ...responseData,

                                    id:
                                        responseData._id,

                                }

                            },


                        providesTags: (
                            result,
                            error,
                            id
                        ) => [

                            {
                                type:
                                    'SessionReferenceContent',

                                id,
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 03 - Create Session Reference Content
                |--------------------------------------------------------------------------
                */

                createSessionReferenceContent:
                    builder.mutation({

                        query:
                            initialContent => ({

                                url:
                                    '/sessionReferenceContent/createSessionReferenceContent',

                                method:
                                    'POST',

                                body: {
                                    ...initialContent,
                                },

                            }),


                        invalidatesTags: [

                            {
                                type:
                                    'SessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 04 - Update Session Reference Content
                |--------------------------------------------------------------------------
                */

                updateSessionReferenceContent:
                    builder.mutation({

                        query:
                            content => ({

                                url:
                                    `/sessionReferenceContent/updateSessionReferenceContent/${content.id || content._id}`,

                                method:
                                    'PATCH',

                                body: {
                                    ...content,
                                },

                            }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'SessionReferenceContent',

                                id:
                                    arg.id || arg._id,
                            },

                            {
                                type:
                                    'SessionReferenceContent',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 05 - Delete Session Reference Content
                |--------------------------------------------------------------------------
                */

                deleteSessionReferenceContent:
                    builder.mutation({

                        query: ({
                            id,
                        }) => ({

                            url:
                                `/sessionReferenceContent/deleteSessionReferenceContent/${id}`,

                            method:
                                'DELETE',

                        }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'SessionReferenceContent',

                                id:
                                    arg.id,
                            },

                            {
                                type:
                                    'SessionReferenceContent',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),

            }),

    })



/*
|--------------------------------------------------------------------------
| 03 - Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetSessionReferenceContentQuery,

    useLazyGetSessionReferenceContentQuery,

    useGetSessionReferenceContentByIdQuery,

    useLazyGetSessionReferenceContentByIdQuery,

    useCreateSessionReferenceContentMutation,

    useUpdateSessionReferenceContentMutation,

    useDeleteSessionReferenceContentMutation,

} =
    sessionReferenceContentApiSlice



/*
|--------------------------------------------------------------------------
| 04 - Base Selector
|--------------------------------------------------------------------------
*/

export const selectSessionReferenceContentResult =

    sessionReferenceContentApiSlice
        .endpoints
        .getSessionReferenceContent
        .select()



/*
|--------------------------------------------------------------------------
| 05 - Content Data Selector
|--------------------------------------------------------------------------
*/

const selectSessionReferenceContentData =

    createSelector(

        selectSessionReferenceContentResult,

        contentResult =>
            contentResult.data

    )



/*
|--------------------------------------------------------------------------
| 06 - Entity Selectors
|--------------------------------------------------------------------------
*/

export const {

    selectAll:
        selectAllSessionReferenceContent,

    selectById:
        selectSessionReferenceContentById,

    selectIds:
        selectSessionReferenceContentIds,

} =

    sessionReferenceContentAdapter
        .getSelectors(

            state =>

                selectSessionReferenceContentData(
                    state
                ) ??

                initialState

        )